import { yupResolver } from '@hookform/resolvers/yup'
import { CountryCode, isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js'
import { Box, Button, Center, Spacer, Text, useBreakpointValue, VStack } from 'native-base'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { NativeSyntheticEvent, Platform, Pressable, TextInputKeyPressEventData, View } from 'react-native'
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view'

import { Alert } from '../../components/Alert'
import { BlockedUserAlert } from '../../components/BlockedUserAlert'
import { ForceLoginModal } from '../../components/ForceLoginModals/ForceLoginModal'
import { RetryForceLoginModal } from '../../components/ForceLoginModals/RetryForceLoginModal'
import HeaderLogo from '../../components/HeaderLogo/HeaderLogo'
import { HorizontalDivider } from '../../components/HorizontalDivider/HorizontalDivider'
import PasswordInput from '../../components/PasswordInput'
import PhoneCountryCode from '../../components/PhoneCountryCode'
import PhoneInput from '../../components/PhoneInput'
import { RegistrationOnboardingAlert } from '../../components/RegistrationOnboardingAlert'
import { Screen } from '../../components/ScreenWrapper/Screen'
import { ShowEnvironmentVariables } from '../../components/ShowEnvironmentVariables'
import { TermsAndConditionsFooter } from '../../components/TermsAndConditionsFooter'
import { useAuth, UserLoginErrorType } from '../../context/auth'
import { useConfig } from '../../context/config'
import { usePhoneState } from '../../data/use-phone-state'
import { useCartProduct } from '../../hooks/use-cart-product'
import { AccountType } from '../../hooks/use-create-standard-profile'
import { RootStackScreenProps } from '../../navigation'
import { useCreateAccountPhoneSchema } from '../../schemas/use-create-account-phone'
import { doesPhoneAlreadyExists } from '../../util/auth'

// import { AuthContinueWithFacebookButton } from './AuthContinueWithFacebookButton'
import { AuthContinueWithGoogleButton } from './AuthContinueWithGoogleButton'
import { LoggedOutHeader } from './LoggedOutHeader'
import { REDIRECTION_REASON_INACTIVITY, REDIRECTION_REASON_UNAUTHORIZED } from './redirect-reasons'
import { WelcomeHeading } from './WelcomeHeading'
import { WelcomeImage } from './WelcomeImage'

type Props = RootStackScreenProps<'Welcome'>

type FormValues = {
  callingCode: string
  phone: string
  password: string
}

export const Welcome: React.FC<Props> = ({ navigation, route }) => {
  const { t } = useTranslation()
  const { config } = useConfig()
  const { phone } = usePhoneState()
  const defaultCountryCode = config.countryCodes[0]
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [validatingPhoneNumber, setValidatingPhoneNumber] = useState(false)
  const [error, setError] = useState<string | null>()
  const [countryCode, setCountryCode] = useState<CountryCode>(defaultCountryCode)
  const [isBlockedUser, setIsBlockedUser] = useState(false)
  const [phoneNumberExists, setPhoneNumberExists] = useState(false)
  const [hasDefaultPhone, setHasDefaultPhone] = useState(false)
  const { verifyPassword, registerPhone } = useAuth()
  const [maxSessionsToken, setMaxSessionsToken] = useState<string | null>(null)
  const [showRetryForceLoginModal, setShowRetryForceLoginModal] = useState(false)
  const { product } = useCartProduct()
  const largeScreenStyles = useBreakpointValue({
    md: { width: '600px', marginLeft: '50%', transform: 'translateX(-50%)' },
  })
  const contentContainerStyle = useMemo(
    () => ({
      maxWidth: '600px',
      paddingLeft: '16px',
      paddingRight: '16px',
      backgroundColor: 'white',
      flexGrow: 1,
      ...largeScreenStyles,
    }),
    [largeScreenStyles]
  )

  const { onLoginSuccess, reason } = route.params ?? {}
  const showLoggedOutHeader = reason === REDIRECTION_REASON_INACTIVITY
  const createAccountPhoneSchema = useCreateAccountPhoneSchema(countryCode, phoneNumberExists)
  const {
    control,
    getValues,
    setValue,
    handleSubmit,
    setError: setFormError,
    formState: { errors = {} } = {},
  } = useForm<FormValues>({
    resolver: yupResolver(createAccountPhoneSchema),
    defaultValues: {
      callingCode: defaultCountryCode,
      phone: '',
      password: '',
    },
  })

  const showRegistrationOnboarding = !!config.partnerConfig?.showRegistrationOnboarding

  useEffect(() => {
    const { number, country } = phone

    if (number) {
      setValue('phone', number)
      setHasDefaultPhone(!!number)
    }

    if (country) {
      const code = country as CountryCode
      setValue('callingCode', code)
      setCountryCode(code)
    }
  }, [phone])

  const onPhoneNumberChange = useCallback(
    async (phoneInput: string) => {
      setValidatingPhoneNumber(true)

      if (phoneInput && isValidPhoneNumber(phoneInput, countryCode)) {
        const numberExists = await doesPhoneAlreadyExists(phoneInput, countryCode)
        setPhoneNumberExists(numberExists)
      } else {
        setPhoneNumberExists(false)
      }

      setValidatingPhoneNumber(false)
    },
    [setValidatingPhoneNumber, countryCode]
  )

  useEffect(() => {
    if (reason === REDIRECTION_REASON_UNAUTHORIZED) {
      setError(t('screens.welcome.signIn.error'))
    }
  }, [reason, t])

  useEffect(() => {
    onPhoneNumberChange(getValues('phone'))
  }, [getValues, countryCode, onPhoneNumberChange])

  const handleGoToHome = useCallback(() => {
    navigation.reset({
      index: 0,
      routes: [{ name: 'Main', params: { screen: 'HomeTab', params: { screen: 'HowCanWeHelp' } } }],
    })
  }, [navigation])

  const handleError = useCallback((errorType: UserLoginErrorType) => {
    switch (errorType) {
      case UserLoginErrorType.BLOCKED:
        setIsBlockedUser(true)
        break
      case UserLoginErrorType.REGISTER:
        setError(t('screens.welcome.register.error'))
        break
    }
  }, [])

  const login = useCallback(
    async (input: FormValues) => {
      if (!input.password) {
        return
      }

      try {
        const phoneNumber = parsePhoneNumber(input.phone, input.callingCode as CountryCode).number
        const result = await verifyPassword(phoneNumber, input.password)

        if (result.kind === 'max-session-reached') {
          setMaxSessionsToken(result.maxSessionsToken)
          return
        } else if (result.kind === 'mfa-check-required') {
          return navigation.navigate('Auth', {
            screen: 'SignInMFA',
            params: {
              token: result.accessToken,
              phone: input.phone,
              countryCode: input.callingCode as CountryCode,
            },
          })
        }

        if (onLoginSuccess) {
          return onLoginSuccess()
        }

        navigation.navigate('AuthRedirect')
      } catch (e) {
        const loginError = e as Error
        if (loginError.message === UserLoginErrorType.BLOCKED) {
          handleError(UserLoginErrorType.BLOCKED)
        } else {
          // Handle Credentials errors (401) at password form field level
          setFormError('password', { type: 'custom', message: t('screens.welcome.signIn.credentialsError') })
        }
      } finally {
        setIsSubmitting(false)
      }
    },
    [handleError, navigation, onLoginSuccess, setFormError, t, verifyPassword]
  )

  const register = useCallback(
    async (input: FormValues) => {
      setIsSubmitting(true)

      try {
        const resp = await registerPhone(input.callingCode as CountryCode, input.phone)

        navigation.navigate('Auth', {
          screen: 'CreateAccountVerify',
          params: {
            accountType: AccountType.PHONE,
            countryCode: input.callingCode as CountryCode,
            phone: input.phone,
            token: resp.data.accessToken,
          },
        })
      } catch (err) {
        handleError(UserLoginErrorType.REGISTER)
      } finally {
        setIsSubmitting(false)
      }
    },
    [handleError, navigation, t, registerPhone]
  )

  const onSubmit: SubmitHandler<FormValues> = useCallback(
    async input => {
      if (validatingPhoneNumber) {
        return
      }

      setIsSubmitting(true)
      setError(null)

      if (phoneNumberExists) {
        await login(input)
      } else {
        await register(input)
      }
    },
    [login, phoneNumberExists, register, validatingPhoneNumber]
  )

  const socialLoginEnabled = config.partnerConfig?.socialLoginEnabled
  const showContinueAsGuest = !product && !hasDefaultPhone
  const showForgotPassword = !showRegistrationOnboarding

  const [passwordInputRef, setPasswordInputRef] = useState<React.RefObject<View>>({ current: null })

  const onEnterKeyPress = useCallback((cb: VoidFunction) => {
    return ({ nativeEvent }: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
      if (nativeEvent.key === 'Enter') {
        cb()
      }
    }
  }, [])

  return (
    <KeyboardAwareScrollView>
      <Screen contentContainerStyle={contentContainerStyle} hasPaddingTop={false}>
        <VStack height={'100%'} justifyContent={'flex-start'}>
          <VStack>
            {error ? (
              <Alert status="error" autofocus>
                {error}
              </Alert>
            ) : null}

            {showLoggedOutHeader && <LoggedOutHeader />}

            <Center>
              <HeaderLogo />
              <WelcomeImage redirectionReason={reason} />
            </Center>

            <WelcomeHeading redirectionReason={reason} />

            {showRegistrationOnboarding && <RegistrationOnboardingAlert />}
          </VStack>
          <VStack space={3}>
            <VStack alignItems="stretch" space="4" width="full" mt={4}>
              <Controller
                name="phone"
                defaultValue=""
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <PhoneInput
                    testID="welcome-screen-phone-input"
                    returnKeyType="next"
                    onKeyPress={onEnterKeyPress(() => passwordInputRef.current?.focus())}
                    onChangeText={input => {
                      onChange(input)
                      onPhoneNumberChange(input)
                    }}
                    value={value}
                    onBlur={onBlur}
                    required
                    placeholder={t('screens.welcome.form.phone.placeholder')}
                    errorMessage={errors.phone?.message}
                    onSubmitEditing={handleSubmit(onSubmit)}
                    inputLeftElement={
                      <Controller
                        name="callingCode"
                        control={control}
                        defaultValue={defaultCountryCode}
                        render={({ field }) => (
                          <PhoneCountryCode
                            nativeID="create-account-country-code"
                            countryCodes={config.countryCodes}
                            defaultValue={defaultCountryCode}
                            selectedValue={countryCode}
                            onValueChange={(code: string) => {
                              setCountryCode(code as CountryCode)
                              field.onChange(code)
                            }}
                          />
                        )}
                      />
                    }
                  />
                )}
              />

              {phoneNumberExists && (
                <Box>
                  <Controller
                    name="password"
                    defaultValue=""
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <PasswordInput
                        onInitRef={setPasswordInputRef}
                        onChangeText={onChange}
                        value={value}
                        onBlur={onBlur}
                        placeholder={t('screens.welcome.form.password.placeholder')}
                        required
                        errorMessage={errors.password?.message}
                        returnKeyType="send"
                        onSubmitEditing={handleSubmit(onSubmit)}
                        testID="welcome-screen-password-input"
                      />
                    )}
                  />
                  {showForgotPassword && (
                    <VStack alignItems="flex-start">
                      <Button
                        variant="link"
                        pt={'12px'}
                        pl={'4px'}
                        testID="welcome-screen-forgot-password-link"
                        onPress={() => navigation.navigate('Auth', { screen: 'ForgotPasswordPhone' })}
                        size="sm"
                      >
                        <Text bold color="primary.500" underline>
                          {t('screens.welcome.signIn.forgotPassword')}
                        </Text>
                      </Button>
                    </VStack>
                  )}
                </Box>
              )}

              {isBlockedUser && <BlockedUserAlert />}
            </VStack>

            <Button
              testID="welcome-screen-continue-button"
              width="full"
              onPress={handleSubmit(onSubmit)}
              isLoading={isSubmitting}
              isLoadingText={t('screens.welcome.submit.loadingLabel')}
              mt={2}
            >
              {t('screens.welcome.submit.label')}
            </Button>

            {socialLoginEnabled || showContinueAsGuest ? (
              <Box>
                <HorizontalDivider>{t('screens.welcome.divider')}</HorizontalDivider>
              </Box>
            ) : null}

            {socialLoginEnabled ? (
              <>
                <AuthContinueWithGoogleButton />

                {/*<AuthContinueWithFacebookButton />*/}
              </>
            ) : null}
          </VStack>
          <VStack space={3} mt={3}>
            {showContinueAsGuest && (
              <Button testID="welcome-screen-guest-button" mt={2} variant="outline" onPress={handleGoToHome}>
                <Text fontSize={'md'} fontWeight={'bold'} color={'primary.500'}>
                  {t('screens.welcome.buttons.guest')}
                </Text>
              </Button>
            )}

            <ShowEnvironmentVariables />

            <Spacer />

            <TermsAndConditionsFooter />
          </VStack>
        </VStack>

        {/*
      On Web dev builds, we can access the dev-only language switcher by going to /language in browser.
      In native app dev builds, this adds an invisible long-press button below the home screen content.
      */}
        {Boolean(__DEV__ && Platform.OS !== 'web') && (
          <Pressable
            // To avoid accidental presses, go to language switcher on a long press only.
            onLongPress={() => navigation.navigate('Language')}
            // Needs an extra-long delay to avoid this React Native bug on Android:
            // https://github.com/facebook/react-native/issues/4944
            delayLongPress={4000}
            // Fill any gap between the content and tabs, for easy pressing.
            // eslint-disable-next-line react-native/no-inline-styles
            style={{ flex: 1, minHeight: 1 }}
            // To be usable if content fills the screen, catch long presses over the last 30px of content.
            hitSlop={{ top: 30 }}
            accessibilityLabel="Go to language switcher"
            accessibilityRole="button"
          />
        )}

        <ForceLoginModal
          isOpen={!!maxSessionsToken && !showRetryForceLoginModal}
          maxSessionsToken={maxSessionsToken}
          onForceLoginSuccess={() => {
            navigation.navigate('AuthRedirect')
          }}
          onForceLoginError={() => {
            setShowRetryForceLoginModal(true)
          }}
          onClose={() => {
            setMaxSessionsToken(null)
          }}
          buttonTestID="force-login-button-singin"
        />
        <RetryForceLoginModal
          isOpen={!!maxSessionsToken && showRetryForceLoginModal}
          maxSessionsToken={maxSessionsToken}
          onForceLoginSuccess={() => {
            navigation.navigate('AuthRedirect')
          }}
          onForceLoginError={() => {
            // Do nothing. We want to keep this modal open.
          }}
          onClose={() => {
            setMaxSessionsToken(null)
          }}
          buttonTestID="retry-force-login-button-singin"
        />
      </Screen>
    </KeyboardAwareScrollView>
  )
}

export default Welcome
